import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Autosuggest from 'react-autosuggest';
import { AddressLookupProps } from '../types';
import ManualAddressForm from './ManualAddressForm';
import regEvent from '../../../../../helpers/gtm/eventPush';
import '../../../../../../sass/components/address_lookup.scss';

import {
  getStoredAddress,
  getInputByName,
  removeStoredAddress,
  setStoredAddress,
  setManualEntryInput,
  setStreetNameInput,
  updateInputByName,
} from '../helpers';

let addressRequest: any = null;
let hasTrackedSearch: boolean = false;
const errorVal: string = 'ERROR';
const notListedVal: string = 'NOT_LISTED';
const requestMinLen: number = 2;
const requestTimeout: number = 300;

const AddressLookup: React.FC<AddressLookupProps> = (props) => {
  const {
    allowManualAddress,
    alsKey,
    alsUrl,
    elementId,
    localisations,
    showExampleAddress,
    storedAddress,
    territory,
    trackingContext,
    inputClass,
  } = props;

  // State management
  const [addressSnippet, setAddressSnippet] = useState<string>('');
  const [addressId, setAddressId] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [manualEntryActive, setManualEntryActive] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);

  /* React Autosuggest events */

  // Function to handle input change event
  const onChange = (
    event: React.FormEvent<HTMLInputElement>,
    { newValue }: Autosuggest.ChangeEvent,
  ): void => {
    setSearchValue(newValue);

    if (newValue === '') {
      setAddressSnippet('');
      setAddressId('');
      updateSelectedAddress({});
      removeStoredAddress();
    }

    // Track address search interaction once
    if (!hasTrackedSearch) {
      regEvent({
        category: 'ads',
        action: `${trackingContext}_st_1:address_interaction`,
        label: `first_character=${newValue}`,
      });
      hasTrackedSearch = true;
    }
  };

  // Function to handle input invalid event
  const onInvalid = (event: React.FormEvent<HTMLInputElement>): void => {
    regEvent({
      category: 'Form error',
      action: `${trackingContext}_st_1:Address`,
      label: 'Please fill in this field',
    });
  };

  // Function to handle input blur event
  const onBlur = (
    event: React.FormEvent<HTMLInputElement>,
    { highlightedSuggestion }: Autosuggest.BlurEvent,
  ): void => {
    // If the user has searched for an address but no result
    // was selected, we want to focus the search input.
    if (searchValue && !addressId) {
      triggerSearchFocus();
    }

    // If an address was selected and the search text has
    // been updated (but NO NEW address result was chosen)
    // restore search text to the previously selected address.
    if (addressId && !highlightedSuggestion) {
      setSearchValue(addressSnippet);
    }

    if (highlightedSuggestion?.id === notListedVal) {
      setSearchValue('');
      setManualEntryActive(true);
      setStoredAddress({
        manual: true,
      });
    }
  };

  // Function to get value of address result
  const getSuggestionValue = (suggestion: any): string => {
    return suggestion.name;
  };

  // Function to render address results
  const renderSuggestion = (suggestion: any): ReactElement => {
    if (suggestion.id === notListedVal) {
      return (
        <span className="react-autosuggest__suggestion__link">
          {suggestion.name}
        </span>
      );
    }
    if (suggestion.id === errorVal) {
      return (
        <span className="react-autosuggest__suggestion__error">
          {suggestion.name}
        </span>
      );
    }
    return <div>{suggestion.name}</div>;
  };

  // Function to clear address results
  const onSuggestionsClearRequested = (): void => {
    setSuggestions([]);
  };

  // Function to handle address selection
  const onSuggestionSelected = (
    event: React.FormEvent<HTMLInputElement>,
    { suggestion }: Autosuggest.SuggestionSelectedEventData<any>,
  ): void => {
    // Not listed
    if (suggestion.id === notListedVal) {
      showManualEntry({ track: true });
      updateSelectedAddress({});
      setTimeout(() => {
        updateInputByName('line_2', searchValue);
        setStoredAddress({
          manual: true,
          line_2: searchValue,
        });
      }, 10);
      return;
    }

    // API error
    if (suggestion.id === errorVal) {
      setSearchValue('');
      updateSelectedAddress({});
      return;
    }

    // Otherwise show address suggestions
    updateSelectedAddress(suggestion);
  };

  // Function to call ALS endpoint
  const onSuggestionsFetchRequested = ({
    value,
  }: Autosuggest.FetchRequestedParams) => {
    const notListedText = localisations?.address_not_listed;
    const enterManuallyText = localisations?.enter_address_manually;

    if (value && value.length >= requestMinLen) {
      clearTimeout(addressRequest);
      addressRequest = setTimeout(() => {
        const text = encodeURIComponent(value);
        axios
          .get(
            `${alsUrl}/addresses?territory=${territory.toLowerCase()}&text=${text}`,
          )
          .then((response) => {
            // Build the result
            const result = response.data.data.map((item: any) => ({
              id: item.uuid,
              name: item.snippet,
            }));
            // Show manual enter option
            if (allowManualAddress && result.length < 5) {
              result.push({
                id: notListedVal,
                name: result.length > 0 ? notListedText : enterManuallyText,
              });
            }
            setSuggestions(result);
          })
          .catch((err) => {
            // Include error message
            const result = [
              {
                id: errorVal,
                name: `${localisations?.error_address_lookup} (${
                  err.toJSON()?.message
                })`,
              },
            ];
            // Show manual enter option
            if (allowManualAddress) {
              result.push({
                id: notListedVal,
                name: enterManuallyText,
              });
            }
            setSuggestions(result);
          });
      }, requestTimeout);
    }
  };

  // Function to set html input values for address id and snippet
  const updateSelectedAddress = useCallback((suggestion: any): void => {
    const { name = null, id = null } = suggestion || {};
    if (id !== notListedVal && id !== errorVal) {
      updateInputByName('address_snippet', name);
      updateInputByName('address_uuid', id);
      setAddressSnippet(name);
      setAddressId(id);
      if (name && id) {
        setStoredAddress({
          manual: false,
          snippet: name,
          id: id,
        });
      }
    }
  }, []);

  // Function to reset manual entry inputs
  const resetManualInputs = (): void => {
    const inputs = document.querySelectorAll<HTMLInputElement>(
      '.address_manual_form input',
    );
    inputs.forEach((input) => {
      input.value = '';
    });
  };

  // Function to show the manual address entry
  const showManualEntry = useCallback(
    (options = { track: false }) => {
      setStoredAddress({ manual: true });
      setManualEntryActive(true);
      setSearchValue('');
      setManualEntryInput('1');
      updateSelectedAddress({});

      if (options.track) {
        regEvent({
          category: 'ads',
          action: `${trackingContext}_enter_address_manually`,
          label: `field=${searchValue}`,
        });
      }
    },
    [searchValue, trackingContext, updateSelectedAddress],
  );

  // Function to hide the backend postcode error
  const hidePostcodeError = () => {
    const postcodeError = document.querySelector('.msg.error');
    if (postcodeError) {
      postcodeError.classList.add('hide-error');
    }
  };

  // Function to trigger seatch input focus, ensures
  // that a lookup is performed after an auto complete.
  const triggerSearchFocus = () => {
    const input = getInputByName('address_lookup_input') as HTMLInputElement;
    if (input) {
      // Use dispatchEvent so focus plays nice with Safari
      const event = new Event('focus');
      input.dispatchEvent(event);
      input.selectionStart = input.value.length;
      input.selectionEnd = input.value.length;
    }
  };

  // Function to hide the manual address entry
  const hideManualEntry = useCallback((): void => {
    removeStoredAddress();
    setManualEntryActive(false);
    setManualEntryInput('0');
    resetManualInputs();
    hidePostcodeError();

    regEvent({
      category: 'ads',
      action: `${trackingContext}_st_1_search_by_address`,
      label: ``,
    });

    // Focus the search field with small delay
    setTimeout(() => {
      const input = getInputByName('address_lookup_input') as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  }, [trackingContext]);

  // Autosuggest input options
  const autoSuggestProps: Autosuggest.InputProps<any> = {
    id: 'address_lookup_input',
    name: 'address_lookup_input',
    className: `react-autosuggest__input ${inputClass}`,
    placeholder: '',
    autoComplete: 'street-address',
    spellCheck: false,
    required: true,
    value: searchValue || '',
    onBlur,
    onChange,
    onInvalid,
    'aria-label': localisations?.address_label,
    'data-testid': 'address_lookup',
    'data-1p-ignore': 'true',
  };

  // Checking for a stored address in the session
  useEffect(() => {
    const { snippet, id } = storedAddress;
    if (snippet && id) {
      updateInputByName('address_snippet', snippet);
      updateInputByName('address_uuid', id);
      setSearchValue(snippet);
      setAddressSnippet(snippet);
      setAddressId(id);
    }
  }, [storedAddress]);

  // Checking for manual entry mode in the session
  useEffect(() => {
    const { manual } = storedAddress;
    if (manual) {
      setManualEntryActive(true);
      setManualEntryInput('1');
    }
  }, [storedAddress]);

  return (
    <>
      {!manualEntryActive ? (
        <>
          <Autosuggest
            suggestions={suggestions}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            onSuggestionSelected={onSuggestionSelected}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            inputProps={autoSuggestProps}
          />
          {showExampleAddress && !suggestions.length ? (
            <p className="form_hint">{localisations.example_address}</p>
          ) : null}
        </>
      ) : null}
      {manualEntryActive ? (
        <>
          <ManualAddressForm
            backButtonType="address"
            hideManualEntry={hideManualEntry}
            localisations={localisations}
            storedAddress={storedAddress}
            setStoredAddress={setStoredAddress}
            getStoredAddress={getStoredAddress}
            setStreetNameInput={setStreetNameInput}
            trackingContext={trackingContext}
            inputClass={inputClass}
          />
        </>
      ) : null}
    </>
  );
};

export default AddressLookup;
